import { useReduxState } from 'hooks';
import { Owners, TitleWithIcon } from 'components/common';
import { CourseTitleWithInfo } from 'components/courses/common';

import type { AdminAdministeredContent, UserWithBasicData } from 'shared-types';

interface Props {
  assets?: AdminAdministeredContent;
  label?: string;
  showJustOnlyOwner?: boolean;
}

const AdminsTransferRightsDisplayAssets = ({
  assets,
  label,
  showJustOnlyOwner = false
}: Props) => {
  const { messages } = useReduxState();

  function getValidAssets<T>(
    allAssets?: (T & {
      onlyAdminGroupOwner: boolean;
      owners: UserWithBasicData[];
    })[]
  ) {
    return !allAssets?.length
      ? []
      : showJustOnlyOwner
      ? allAssets.filter(
          (asset) => !asset.onlyAdminGroupOwner && asset.owners.length === 1
        )
      : allAssets;
  }

  const achievements = getValidAssets(assets?.achievements);
  const courses = getValidAssets(assets?.courses);
  const events = getValidAssets(assets?.events);
  const groups = getValidAssets(assets?.groups);
  const paths = getValidAssets(assets?.paths);

  const shouldDisplay =
    achievements.length ||
    courses.length ||
    events.length ||
    groups.length ||
    paths.length;

  return shouldDisplay ? (
    <div className="resources">
      {label ? <div className="label">{label}</div> : null}

      {courses.length ? (
        <div className="section">
          <div className="section-title">{messages.commonCourses}</div>

          {courses.map((course) => (
            <div className="course" key={course.id}>
              <CourseTitleWithInfo linkToCourseAdminPage={true} course={course}>
                <Owners owners={course.owners} />
              </CourseTitleWithInfo>
            </div>
          ))}
        </div>
      ) : null}

      {groups.length ? (
        <div className="section">
          <div className="section-title">{messages.commonGroups}</div>

          {groups.map((group) => (
            <div className="group" key={group.id}>
              <TitleWithIcon
                icon="users"
                title={group.title}
                titleLink={{
                  as: `/groups/${group.id}`,
                  href: '/groups/[groupId]'
                }}
              >
                <Owners owners={group.owners} />
              </TitleWithIcon>
            </div>
          ))}
        </div>
      ) : null}

      {paths.length ? (
        <div className="section">
          <div className="section-title">{messages.paths}</div>

          {paths.map((path) => (
            <div className="path" key={path.id}>
              <TitleWithIcon
                icon="tasks"
                title={path.title}
                titleLink={{
                  as: `/admin/paths/${path.id}`,
                  href: '/admin/paths/[pathId]'
                }}
              >
                <Owners owners={path.owners} />
              </TitleWithIcon>
            </div>
          ))}
        </div>
      ) : null}

      {achievements.length ? (
        <div className="section">
          <div className="section-title">{messages.commonAchievementsAlt}</div>

          {achievements.map((achievement) => (
            <div className="achievement" key={achievement.id}>
              <TitleWithIcon
                icon="trophy"
                title={achievement.title}
                titleLink={{
                  as: `/admin/achievements/${achievement.id}`,
                  href: '/admin/achievements/[achievementId]'
                }}
              >
                <Owners owners={achievement.owners} />
              </TitleWithIcon>
            </div>
          ))}
        </div>
      ) : null}

      {events.length ? (
        <div className="section">
          <div className="section-title">{messages.eventCalendarEvent}</div>

          {events.map((event) => (
            <div className="event" key={event.id}>
              <CourseTitleWithInfo
                linkToCourseAdminPage={false}
                linkAs={`/calendar/${event.id}`}
                linkHref="/calendar/[calendarEventId]"
                course={event}
              >
                <Owners owners={event.owners} />
              </CourseTitleWithInfo>
            </div>
          ))}
        </div>
      ) : null}

      <style jsx>{`
        .resources {
          display: flex;
          flex-direction: column;
          gap: 20px;
          margin-bottom: 30px;
        }
        .section-title {
          font-size: 1.4rem;
          font-weight: 700;
          margin-bottom: 10px;
        }
        .achievement,
        .course,
        .event,
        .group,
        .path {
          margin-top: 10px;
        }
      `}</style>
    </div>
  ) : null;
};

export default AdminsTransferRightsDisplayAssets;
